/**
 * Helper utility functions for the SalesGro application
 */
import { Link as RouterLink } from 'react-router-dom';
import { Link as MuiLink } from '@mui/material';
import {
  CurrencyRupee as CurrencyRupeeIcon,
  CheckCircle as CheckCircleIcon,
  AccessTime as AccessTimeIcon,
  ErrorOutline as ErrorOutlineIcon,
  HourglassEmpty as HourglassEmptyIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';
import api from '../services/api';

/**
 * Format phone number with country code
 * @param {string} countryCode - Country code (e.g., "+91")
 * @param {string} phoneNumber - Phone number without country code
 * @returns {string} - Formatted phone number
 */
export const formatPhoneNumber = (countryCode, phoneNumber) => {
  if (!phoneNumber) return '';
  
  // Remove any non-digit characters
  const digits = phoneNumber.replace(/\D/g, '');
  
  // Format based on country code
  switch (countryCode) {
    case '+1': // US/Canada
      if (digits.length === 10) {
        return `${countryCode} (${digits.substring(0, 3)}) ${digits.substring(3, 6)}-${digits.substring(6)}`;
      }
      break;
    case '+91': // India
      if (digits.length === 10) {
        return `${countryCode} ${digits.substring(0, 5)} ${digits.substring(5)}`;
      }
      break;
    // Add more country-specific formatting as needed
    default:
      // Generic formatting for other country codes
      return `${countryCode} ${digits}`;
  }
  
  // Default fallback if specific formatting not applied
  return `${countryCode} ${digits}`;
};

/**
 * Format currency with appropriate symbol
 * @param {number} amount - Amount to format
 * @param {string} currency - Currency code (default: 'INR')
 * @returns {string} - Formatted currency string
 */
export const formatCurrency = (amount, currency = 'INR') => {
  if (amount === null || amount === undefined) return '—';
  
  const currencySymbols = {
    'INR': '₹',
    'USD': '$',
    'EUR': '€',
    'GBP': '£'
  };
  
  const symbol = currencySymbols[currency] || '';
  
  return `${symbol}${parseFloat(amount).toLocaleString('en-IN')}`;
};

/**
 * Get human-readable date/time from ISO string or Date object
 * @param {string|Date} dateTime - ISO date string or Date object
 * @param {string} format - Format type ('date', 'time', 'datetime', 'relative')
 * @returns {string} - Formatted date/time string
 */
export const formatDateTime = (dateTime, format = 'datetime') => {
  if (!dateTime) return '';
  
  const date = typeof dateTime === 'string' ? new Date(dateTime) : dateTime;
  
  // Check if date is valid
  if (isNaN(date.getTime())) return '';
  
  const options = {
    date: { day: 'numeric', month: 'short', year: 'numeric' },
    time: { hour: 'numeric', minute: 'numeric', hour12: true },
    datetime: { 
      day: 'numeric', month: 'short', year: 'numeric',
      hour: 'numeric', minute: 'numeric', hour12: true
    }
  };
  
  if (format === 'relative') {
    const now = new Date();
    const diffMs = now - date;
    const diffSec = Math.floor(diffMs / 1000);
    const diffMin = Math.floor(diffSec / 60);
    const diffHour = Math.floor(diffMin / 60);
    const diffDay = Math.floor(diffHour / 24);
    
    if (diffSec < 60) return 'Just now';
    if (diffMin < 60) return `${diffMin}m ago`;
    if (diffHour < 24) return `${diffHour}h ago`;
    if (diffDay < 7) return `${diffDay}d ago`;
    
    // Fall back to date format for older dates
    return date.toLocaleDateString('en-US', options.date);
  }
  
  return date.toLocaleString('en-US', options[format] || options.datetime);
};

/**
 * Truncate text to specified length with ellipsis
 * @param {string} text - Text to truncate
 * @param {number} maxLength - Maximum length before truncation
 * @returns {string} - Truncated text with ellipsis if needed
 */
export const truncateText = (text, maxLength = 50) => {
  if (!text) return '';
  
  return text.length <= maxLength
    ? text
    : `${text.substring(0, maxLength)}...`;
};

/**
 * Get initials from name (up to 2 characters)
 * @param {string} name - Full name
 * @returns {string} - Initials (up to 2 chars)
 */
export const getInitials = (name) => {
  if (!name) return '';
  
  const parts = name.split(' ').filter(Boolean);
  
  if (parts.length === 0) return '';
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
};

/**
 * Convert camelCase or snake_case to Title Case with spaces
 * @param {string} str - String to convert
 * @returns {string} - Converted string
 */
export const toTitleCase = (str) => {
  if (!str) return '';
  
  // Handle snake_case
  if (str.includes('_')) {
    return str
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  }
  
  // Handle camelCase
  return str
    // Insert a space before all caps and uppercase the first character
    .replace(/([A-Z])/g, ' $1')
    // Upper case first character of the string
    .replace(/^./, function(str) { return str.toUpperCase(); })
    .trim();
};

// Common styles for Autocomplete
export const autocompleteStyles = {
  '& .MuiInputBase-root': {
    fontSize: '0.75rem',
    minHeight: '32px',
    fontWeight: 400
  },
  '& .MuiInputBase-input': {
    fontSize: '0.75rem',
    fontWeight: 400
  },
  '& .MuiAutocomplete-endAdornment': {
    top: '50%',
    transform: 'translateY(-50%)'
  },
  '& .MuiAutocomplete-popupIndicator': {
    padding: '2px'
  },
  '& .MuiAutocomplete-clearIndicator': {
    padding: '2px'
  }
};


  // Helper function to get labels from default options
  export const getLabelsFromDefaults = (savedValues, defaultOptions) => {
    if (!savedValues || !savedValues.length) return [];
    
    // Create a map of type to label from default options
    const optionsMap = defaultOptions.reduce((map, option) => {
      map[option.type] = option.label;
      return map;
    }, {});
    
    // Map saved values to their labels or use the value itself if no label is found
    return savedValues.map(value => {
      // If value is already a string, try to find its label or use the value
      if (typeof value === 'string') {
        return optionsMap[value] || value;
      }
      // If value is an object with type, use that to find label
      if (value.type) {
        return optionsMap[value.type] || value.type;
      }
      // If value is an object with label, use that
      if (value.label) {
        return value.label;
      }
      // Fallback to a string representation
      return JSON.stringify(value);
    });
  };


  // Payment status → MUI color scheme
  export const getPaymentStatusColor = (status) => {
    switch (status) {
      case 'draft':
        return 'secondary'; // Not yet invoiced
      case 'pending':
        return 'warning'; // Awaiting payment
      case 'partially_paid':
        return 'info'; // Partial progress
      case 'paid':
      case 'completed':
        return 'success'; // Payment done
      case 'refunded':
        return 'error'; // Refunded fully
      case 'failed':
        return 'error'; // Failed transaction
      case 'partially_refunded':
        return 'warning'; // Partial reversal
      default:
        return 'default'; // fallback
    }
  };


  export const getPaymentStatusProps = (status, status_verbose) => {
    const status_color = getPaymentStatusColor(status);
    switch ((status || '').toLowerCase()) {
      case 'pending':
        return { icon: <HourglassEmptyIcon />, color: status_color, label: status_verbose };
      case 'partial':
        return { icon: <HourglassEmptyIcon />, color: status_color, label: status_verbose };
      case 'completed':
        return { icon: <CheckCircleIcon />, color: status_color, label: status_verbose };
      case 'failed':
        return { icon: <CancelIcon />, color: status_color, label: status_verbose };
      case 'refunded':
        return { icon: <CancelIcon />, color: status_color, label: status_verbose };
      case 'partially_refunded':
        return { icon: <CancelIcon />, color: status_color, label: status_verbose };
      default:
        return { icon: <HourglassEmptyIcon />, color: status_color, label: status_verbose };
    }
  };



  export const generateFormatedLink = (link, text) => {
    return (
      <MuiLink
        to={link}
        component={RouterLink}
        sx={{
          textDecoration: 'none',
          color: 'text.primary',
          '&:hover': {
            color: 'success.main',
            textDecoration: 'underline'
          }
        }}
      >
        {text}
      </MuiLink>
    );
  };

  export const getAccountStatusColor = (status) => {
    switch (status) {
      case 'PE':
        return 'warning';
      case 'AP':
        return 'success';
      case 'RJ':
        return 'error';
      case 'HO':
        return 'info';
      default:
        return 'default';
    }
  };

export default {
  getAccountStatusColor,
  formatPhoneNumber,
  formatCurrency,
  formatDateTime,
  truncateText,
  getInitials,
  toTitleCase,
  autocompleteStyles,
  getLabelsFromDefaults,
  getPaymentStatusColor,
  generateFormatedLink,
  getPaymentStatusProps,
};

/**
 * Format currency in Indian format with lakhs (L) and crores (Cr)
 * @param {number|string} value - Amount to format
 * @returns {string} - Formatted amount like "50L" or "1.5Cr"
 */
export const formatIndianCurrency = (value) => {
  if (value === null || value === undefined || value === '') return 'Any';
  
  // Convert to number if it's a string
  const numValue = typeof value === 'string' ? parseFloat(value) : value;
  
  if (isNaN(numValue)) return 'Any';
  
  // Format based on value
  if (numValue >= 10000000) {
    // If >= 1 crore, show as Cr
    return `${(numValue / 10000000).toFixed(1)}Cr`;
  } else if (numValue >= 100000) {
    // Show as lakhs
    return `${(numValue / 100000).toFixed(0)}L`;
  } else if (numValue >= 1000) {
    // Show as thousands
    return `${(numValue / 1000).toFixed(0)}K`;
  }
  
  return `₹${numValue}`;
};

/**
 * Format budget range in Indian format 
 * @param {object} budget - Budget object with min and max values
 * @returns {string} - Formatted budget like "50L-1.5Cr"
 */
export const formatBudgetRange = (budget) => {
  if (!budget) return "Not specified";
  
  const { min, max } = budget;
  
  if (!min && !max) return "Not specified";
  
  return `${formatIndianCurrency(min)}-${formatIndianCurrency(max)}`;
};

export const getInvoiceStatusColor = (status) => {
  console.log('status', status);
  const statusColors = {
    'UP': 'error', // Unpaid
    'PD': 'success', // Paid
    'PP': 'warning', // Partially Paid
    'OV': 'error', // Overdue
    'RP': 'info', // Refunded
    'CN': 'default' // Cancelled
  };
  return statusColors[status] || 'default';
};

export const getPaymentModeIcon = (mode) => {
  const modeIcons = {
    'CR': '💳', // Credit Card
    'DB': '💳', // Debit Card
    'CS': '💰', // Cash
    'BT': '🏦' // Bank Transfer
  };
  return modeIcons[mode] || '💳';
};

export const formatDate = (date) => {
  return new Date(date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

export const formatDuration = (duration) => {
  const hours = Math.floor(duration / 3600);
  const minutes = Math.floor((duration % 3600) / 60);
  const seconds = duration % 60;
  return `${hours}h ${minutes}m ${seconds}s`;
};

export const getQueryStatusColor = (status) => {
  switch (status) {
    case 'NE':
      return 'warning';
    case 'IP':
      return 'info';
    case 'RD':
      return 'success';
    case 'PE':
      return 'warning';
    default:
      return 'default';
  }
};

/**
 * Render invoice status column with proper styling and icons
 * @param {string} status - The invoice status code
 * @param {string} status_verbose - The verbose status text
 * @param {object} options - Additional options for rendering
 * @returns {object} - Object with icon name, text, and color for rendering
 */
export const renderInvoiceColumn = (status, status_verbose, options = {}) => {
  const {
    showIcon = true,
    sx = {}
  } = options;

  // Status configuration with icons and colors
  const statusConfig = {
    'PD': {
      label: 'Paid',
      color: 'success',
      icon: 'CheckCircle',
      verbose: 'Paid'
    },
    'UP': {
      label: 'Unpaid',
      color: 'warning',
      icon: 'Warning',
      verbose: 'Unpaid'
    },
    'PP': {
      label: 'Pending',
      color: 'info',
      icon: 'Schedule',
      verbose: 'Pending'
    },
    'CN': {
      label: 'Cancelled',
      color: 'error',
      icon: 'Cancel',
      verbose: 'Cancelled'
    },
    'OV': {
      label: 'Overdue',
      color: 'error',
      icon: 'Error',
      verbose: 'Overdue'
    },
    'RP': {
      label: 'Refunded',
      color: 'info',
      icon: 'Refresh',
      verbose: 'Refunded'
    }
  };

  const config = statusConfig[status] || {
    label: status_verbose || 'Unknown',
    color: 'default',
    icon: 'Help',
    verbose: status_verbose || 'Unknown'
  };

  return {
    icon: showIcon ? config.icon : null,
    text: config.verbose,
    color: config.color,
    sx: {
      fontWeight: 600,
      textTransform: 'uppercase',
      fontSize: '0.75rem',
      ...sx
    }
  };
};

/**
 * Fetch subscription filters from backend
 * @returns {Promise<Array>} - Array of subscription filter options
 */
export const fetchSubscriptionFilters = async () => {
  try {
    const response = await api.get('subscription-filter/');
    if (response.data.success) {
      return response.data.data || [];
    }
    return [];
  } catch (error) {
    console.error('Error fetching subscription filters:', error);
    return [];
  }
};

/**
 * Fetch query status filters from backend
 * @returns {Promise<Object>} - Object containing data and transitions
 */
export const fetchQueryStatusFilters = async () => {
  try {
    const response = await api.get('query-status-filter/');
    if (response.data.success) {
      return {
        data: response.data.data || [],
        transitions: response.data.transitions || {}
      };
    }
    return { data: [], transitions: {} };
  } catch (error) {
    console.error('Error fetching query status filters:', error);
    return { data: [], transitions: {} };
  }
};

/**
 * Fetch invoice status filters from backend
 * @returns {Promise<Array>} - Array of invoice status filter options
 */
export const fetchInvoiceStatusFilters = async () => {
  try {
    const response = await api.get('invoice-status-filter/');
    if (response.data.success) {
      return response.data.data || [];
    }
    return [];
  } catch (error) {
    console.error('Error fetching invoice status filters:', error);
    return [];
  }
};

/**
 * Fetch web message status filters from backend
 * @returns {Promise<Object>} - Object containing data and transitions
 */
export const fetchWebMessageStatusFilters = async () => {
  try {
    const response = await api.get('web-message-status-filter/');
    if (response.data.success) {
      return {
        data: response.data.data || [],
        transitions: response.data.transitions || {}
      };
    }
    return { data: [], transitions: {} };
  } catch (error) {
    console.error('Error fetching web message status filters:', error);
    return { data: [], transitions: {} };
  }
};

/**
 * Fetch query type filters from backend
 * @returns {Promise<Array>} - Array of query type filter options
 */
export const fetchQueryTypeFilters = async () => {
  try {
    const response = await api.get('query-type-filter/');
    if (response.data.success) {
      return response.data.data || [];
    }
    return [];
  } catch (error) {
    console.error('Error fetching query type filters:', error);
    return [];
  }
};

/**
 * Fetch item type filters from backend
 * @returns {Promise<Array>} - Array of item type filter options
 */
export const fetchItemTypeFilters = async () => {
  try {
    const response = await api.get('item-type-filter/');
    if (response.data.success) {
      return response.data.data || [];
    }
    return [];
  } catch (error) {
    console.error('Error fetching item type filters:', error);
    return [];
  }
};

/**
 * Fetch payment mode filters from backend
 * @returns {Promise<Array>} - Array of payment mode filter options
 */
export const fetchPaymentModeFilters = async () => {
  try {
    const response = await api.get('payment-mode-filter/');
    if (response.data.success) {
      return response.data.data || [];
    }
    return [];
  } catch (error) {
    console.error('Error fetching payment mode filters:', error);
    return [];
  }
};

/**
 * Fetch subscription plans from backend
 * @returns {Promise<Array>} - Array of active subscription plans
 */
export const fetchSubscriptionPlans = async () => {
  try {
    const response = await api.get('plans/');
    if (response.data.success) {
      return response.data.data || [];
    }
    return [];
  } catch (error) {
    console.error('Error fetching subscription plans:', error);
    return [];
  }
};